import {
  Box,
  Chip,
  createStyles,
  Grid,
  makeStyles,
  MenuItem,
  Select,
  FormHelperText,
  InputLabel,
} from '@material-ui/core';
import React, { useState, useEffect, ChangeEvent } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { removeSpecialChars } from '../../../../../services/mask';
import ButtonComponent from '../../../../atoms/button';
import Input from '../../../../atoms/input';
import Typography from '../../../../atoms/typography';
import { vehicleSchema, SingleVehicleForm } from './schema';
import colors from '../../../../../assets/styles/colors';
import commons from '../../../../../assets/styles/commons';
import { ReactComponent as InfoIcon } from '../../../../../assets/icons/icon-info.svg';
import { useHistory, useParams } from 'react-router-dom';
import ModalCustom from '../../../../atoms/modalCustom';
import ImageUploadField from '../../../../atoms/imageUploadFile';
import useUpdatePrivateVehicleData from '../../../../../hooks/vehicle/useUpdatePrivateVehicleData';
import useGetVehicleTypes from '../../../../../hooks/vehicleType/useGetVehicleTypes';
import useGetPrivateVehicleById from '../../../../../hooks/vehicle/useGetPrivateVehiclesById';

interface FormErrors {
  [key: string]: string | FormErrors | FormErrors[] | undefined;
  vehicles?: FormErrors[];
}

const useStyles = makeStyles(() =>
  createStyles({
    chip: {
      backgroundColor: colors.neutral.superLightGray,
      color: colors.secondary.intenseNavy,
      border: '1px solid #8F9BB3',
      borderRadius: '8px',
      padding: 5,
      fontFamily: commons.fontFamily,
      fontSize: 14,
      flex: 1,
      display: 'flex',
      justifyContent: 'flex-start',
      fontVariant: 'lining-nums',
    },
    selectLabel: {
      marginBottom: '4px',
      color: colors.neutral.darkGray,
    },
    crlvUploadField: {
      marginTop: '16px',
    },
    formActions: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: '32px',
      gap: '16px',
      flexWrap: 'wrap',
    },
  }),
);

const CreateVehiclesForm: React.FC<any> = ({ onClick }: any) => {
  const classes = useStyles();
  const history = useHistory();
  const { id: profileId } = useParams<{ id: string }>();
  const { execute: getVehiclesTypesService } = useGetVehicleTypes()
  const {
    data: fetchedVehicle
  }: any = useGetPrivateVehicleById(profileId || '');
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [vehiclesTypes, setVehiclesTypes] = useState<any>([]);
  const [vehicles, setVehicles] = useState<SingleVehicleForm[]>([
    {
      id: '',
      model: '',
      year: '',
      licensePlate: '',
      payloadCapacity: 0,
      vehicleType: '',
      dimensions: {
        weight: 0,
        length: 0,
        width: 0,
      },
      crlv: {
        crlvId: '',
        documentNumber: '',
        expirationDate: '',
        isDigital: false,
        imageUrlFront: null,
        imageUrlBack: null,
      },
      images: {
        imageUrlBack: null,
        imageUrlFront: null,
        imageUrlLeft: null,
        imageUrlRight: null,
      },
    },
  ]);

  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const handleEditVehicle = () => {
    const edtVehicle: SingleVehicleForm = {
      id: fetchedVehicle?.vehicleId,
      model: fetchedVehicle?.model,
      year: fetchedVehicle?.year,
      licensePlate: fetchedVehicle?.plate,
      payloadCapacity: fetchedVehicle?.payloadCapacity,
      vehicleType: fetchedVehicle.vehicleType?.vehicleTypeId || '',
      dimensions: {
        weight: fetchedVehicle?.weight,
        length: fetchedVehicle?.chestLength,
        width: fetchedVehicle?.chestWidth,
      },
      crlv: {
        crlvId: fetchedVehicle.crlv?.crlvId || '',
        documentNumber: fetchedVehicle.crlv?.documentNumber || '',
        expirationDate: fetchedVehicle.crlv?.expirationDate,
        isDigital: fetchedVehicle.crlv?.isDigital || false,
        imageUrlFront: fetchedVehicle.crlv?.frontImageUrl || null,
        imageUrlBack: fetchedVehicle.crlv?.backImageUrl || null,
      },
      images: {
        imageUrlBack: fetchedVehicle.images?.imageUrlBack || null,
        imageUrlFront: fetchedVehicle.images?.imageUrlFront || null,
        imageUrlLeft: fetchedVehicle.images?.imageUrlLeft || null,
        imageUrlRight: fetchedVehicle.images?.imageUrlRight || null,
      },
    }
    setVehicles([{ ...edtVehicle }]);
  }

  useEffect(() => {
    if (profileId && fetchedVehicle) {
      handleEditVehicle();
    }
    getVehiclesTypes()
  }, [profileId, fetchedVehicle])

  useEffect(() => {
    const handler = setTimeout(() => {
      validateForm();
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [vehicles]);

  useEffect(() => {
    getVehiclesTypes()
  }, [])

  const handleInputChange = (
    index: number,
    field: keyof SingleVehicleForm,
    value: any,
  ) => {
    const updatedVehicles = [...vehicles];
    if (field === 'year' || field === 'payloadCapacity') {
      value = value === '' ? 0 : parseInt(value, 10) || 0;
    }
    (updatedVehicles[index] as any)[field] = value;
    setVehicles(updatedVehicles);
  };

  const handleNestedInputChange = (
    index: number,
    parentField: 'crlv' | 'dimensions' | 'images',
    nestedField: string,
    value: any,
  ) => {
    const updatedVehicles = [...vehicles];
    const updatedNestedObject = {
      ...(updatedVehicles[index][parentField] as any),
      [nestedField]: value,
    };
    if (parentField === 'dimensions') {
      if (
        nestedField === 'weight' ||
        nestedField === 'length' ||
        nestedField === 'width'
      ) {
        updatedNestedObject[nestedField] =
          value === '' ? 0 : (value.includes(',') ? value.replace(',', '.') : value) || 0;
      }
    }
    (updatedVehicles[index] as any)[parentField] = updatedNestedObject;
    setVehicles(updatedVehicles);
  };

  const validateForm = async () => {
    try {
      await vehicleSchema.validate({ vehicles }, { abortEarly: false });
      setFormErrors({});
      return true;
    } catch (err: any) {
      const newErrors: FormErrors = {};
      if (err.inner) {
        err.inner.forEach((error: any) => {
          const pathParts = error.path.split('.');
          const ensurePath = (obj: FormErrors, parts: string[], value: any) => {
            let current = obj;
            for (let i = 0; i < parts.length; i++) {
              const part = parts[i];
              const isLast = i === parts.length - 1;
              const arrayMatch = part.match(/(\w+)\[(\d+)\]/);

              if (arrayMatch) {
                const arrayName = arrayMatch[1];
                const index = parseInt(arrayMatch[2], 10);
                if (!current[arrayName]) {
                  current[arrayName] = [];
                }
                if (!(current[arrayName] as FormErrors[])[index]) {
                  (current[arrayName] as FormErrors[])[index] = {};
                }
                current = (current[arrayName] as FormErrors[])[index];
              } else if (!isLast) {
                if (!current[part]) {
                  current[part] = {};
                }
                current = current[part] as FormErrors;
              } else {
                current[part] = value;
              }
            }
          };
          ensurePath(newErrors, pathParts, error.message);
        });
      }
      setFormErrors(newErrors);
      return false;
    }
  };

  const { execute, loading } = useUpdatePrivateVehicleData();

  const updateVehicles = async (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = await validateForm();

    if (!isValid) {
      console.log('Formulário inválido, verifique os erros:', formErrors);
      return;
    }

    try {

      const vehicle = vehicles[0]

      await execute(fetchedVehicle?.vehicleId, {
        model: vehicle.model,
        year: vehicle.year.toString().substring(0, 4),
        licensePlate: vehicle.licensePlate,
        payloadCapacity: String(vehicle.payloadCapacity) || "0",
        vehicleTypeId: vehicle.vehicleType,
        dimensions: {
          weight: Number(vehicle.dimensions.weight) || 0,
          length: Number(vehicle.dimensions.length) || 0,
          width: Number(vehicle.dimensions.width) || 0,
        },
        crlv: {
          crlvId: vehicle.crlv.crlvId,
          documentNumber: vehicle.crlv.documentNumber,
          expirationDate: vehicle.crlv.expirationDate,
          isDigital: vehicle.crlv.isDigital,
          imageUrlFront: vehicle.crlv.imageUrlFront,
          imageUrlBack: vehicle.crlv.imageUrlBack,
        },
        images: {
          imageUrlFront: vehicle.images.imageUrlFront,
          imageUrlBack: vehicle.images.imageUrlBack,
          imageUrlLeft: vehicle.images.imageUrlLeft,
          imageUrlRight: vehicle.images.imageUrlRight,
        },
      }, () => {
        history.goBack();
      },
      );
    } catch (error) {
      console.error('Erro ao editar veículos:', error);
    }
  };

  const getVehiclesTypes = async () => {

    try {

      const response: any = await getVehiclesTypesService(1, '', 1000)

      const mappedvehiclesTypes = response.rows.map((vehicleType: any) => ({
        label: vehicleType.vehicleType,
        value: vehicleType.vehicleTypeId,
      }));

      setVehiclesTypes(mappedvehiclesTypes)

    } catch (error) {
      console.error('Erro buscar tipos de veiculos:', error);
      toast.error('Erro ao carregar tipos de veículos.');
    }
  };

  const cancelOperation = () => {
    history.goBack();
  };

  const isDirty = React.useMemo(() => {
    if (Object.keys(formErrors).length > 0) return true;

    if (vehicles.length > 1) return true;

    if (vehicles.length === 1) {
      const vehicleTypes = {
        CAMINHAO_TOCO: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
        CARRO_PEQUENO: 'yyyyyyyy-yyyy-yyyy-yyyy-yyyyyyyyyyyy',
      };

      const defaultVehicle: SingleVehicleForm = {
        id: '',
        model: '',
        year: new Date().getFullYear().toString(),
        licensePlate: '',
        payloadCapacity: 0,
        vehicleType: '',
        dimensions: { weight: 0, length: 0, width: 0 },
        crlv: {
          crlvId: '',
          documentNumber: '',
          expirationDate: '',
          isDigital: false,
          imageUrlFront: null,
          imageUrlBack: null,
        },
        images: {
          imageUrlBack: null,
          imageUrlFront: null,
          imageUrlLeft: null,
          imageUrlRight: null,
        },
      };

      const currentVehicle = vehicles[0];
      let hasChanges = false;

      const compareValues = (val1: any, val2: any) => {
        if (val1 instanceof File && val2 instanceof File) {
          return (
            val1 !== val2 || val1.name !== val2.name || val1.size !== val2.size
          );
        }
        if (
          (val1 instanceof File && val2 === null) ||
          (val1 === null && val2 instanceof File)
        ) {
          return true;
        }
        if (
          (val1 instanceof File && typeof val2 === 'string') ||
          (typeof val1 === 'string' && val2 instanceof File)
        ) {
          return true;
        }
        return val1 !== val2;
      };

      for (const key in currentVehicle) {
        if (key === 'id') continue;

        const typedKey = key as keyof SingleVehicleForm;
        const defaultValue = defaultVehicle[typedKey];
        const currentValue = currentVehicle[typedKey];

        if (
          typeof currentValue === 'object' &&
          currentValue !== null &&
          typeof defaultValue === 'object' &&
          defaultValue !== null
        ) {
          const nestedCurrent = currentValue as any;
          const nestedDefault = defaultValue as any;

          for (const nestedKey in nestedCurrent) {
            if (
              compareValues(nestedCurrent[nestedKey], nestedDefault[nestedKey])
            ) {
              hasChanges = true;
              break;
            }
          }
        } else if (compareValues(currentValue, defaultValue)) {
          hasChanges = true;
          break;
        }
        if (hasChanges) break;
      }
      return hasChanges;
    }
    return false;
  }, [vehicles, formErrors]);

  const decideToCancelOperation = () => {
    if (!isDirty) cancelOperation();
    else setOpenCancelModal(true);
  };

  const isFormValid =
    Object.keys(formErrors).length === 0 && vehicles.length > 0;

  return (
    <Box style={{ display: 'flex', gap: 20, flexDirection: 'column' }}>
      <Typography fontWeight="700" color="secondary">
        Adicionar veículos
      </Typography>
      {!isFormValid && <Chip
        label="É necessário adicionar as informações completas dos veículos."
        icon={<InfoIcon />}
        className={classes.chip}
      />}

      <Box>
        <form onSubmit={updateVehicles}>
          {vehicles.map((vehicle, index) => {
            const vehicleErrors = (
              formErrors.vehicles && Array.isArray(formErrors.vehicles)
                ? formErrors.vehicles[index]
                : {}
            ) as FormErrors;

            const crlvErrors = vehicleErrors.crlv as FormErrors;
            const dimensionsErrors = vehicleErrors.dimensions as FormErrors;
            const imagesErrors = vehicleErrors.images as FormErrors;

            return (
              <Grid
                container
                spacing={2}
                key={vehicle.id}
                style={{
                  marginBottom: '20px',
                  border: '1px solid #ccc',
                  padding: '10px',
                  borderRadius: '8px',
                  position: 'relative',
                }}
              >
                <Grid item xs={12}>
                  <Typography
                    fontWeight="bold"
                    style={{ marginBottom: '10px' }}
                  >
                    Informações gerais
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Input
                    size="small"
                    label="Modelo"
                    placeholder="Caminhão"
                    value={vehicle.model}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleInputChange(index, 'model', e.target.value)
                    }
                    error={!!vehicleErrors.model}
                    helperText={vehicleErrors.model as string}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Input
                    size="small"
                    label="Ano"
                    placeholder="2025"
                    value={vehicle.year || ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleInputChange(index, 'year', e.target.value)
                    }
                    error={!!vehicleErrors.year}
                    helperText={vehicleErrors.year as string}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Input
                    size="small"
                    label="Placa"
                    placeholder="AAA1234"
                    value={vehicle.licensePlate}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleInputChange(
                        index,
                        'licensePlate',
                        removeSpecialChars(e.target.value).toUpperCase(),
                      )
                    }
                    error={!!vehicleErrors.licensePlate}
                    helperText={vehicleErrors.licensePlate as string}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Input
                    size="small"
                    label="Capacidade de carga (kg)"
                    placeholder="800"
                    value={vehicle.payloadCapacity || ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleInputChange(
                        index,
                        'payloadCapacity',
                        e.target.value,
                      )
                    }
                    error={!!vehicleErrors.payloadCapacity}
                    helperText={vehicleErrors.payloadCapacity as string}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Input
                    size="small"
                    label="Peso (kg)"
                    placeholder="215.77"
                    value={vehicle.dimensions.weight || ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleNestedInputChange(
                        index,
                        'dimensions',
                        'weight',
                        e.target.value,
                      )
                    }
                    error={!!dimensionsErrors?.weight}
                    helperText={dimensionsErrors?.weight as string}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Input
                    size="small"
                    label="Comprimento do baú (m)"
                    placeholder="1.32"
                    value={vehicle.dimensions.length || ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleNestedInputChange(
                        index,
                        'dimensions',
                        'length',
                        e.target.value,
                      )
                    }
                    error={!!dimensionsErrors?.length}
                    helperText={dimensionsErrors?.length as string}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Input
                    size="small"
                    label="Largura do baú (m)"
                    placeholder="0.70"
                    value={vehicle.dimensions.width || ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleNestedInputChange(
                        index,
                        'dimensions',
                        'width',
                        e.target.value,
                      )
                    }
                    error={!!dimensionsErrors?.width}
                    helperText={dimensionsErrors?.width as string}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <InputLabel
                    id={`vehicle-type-label-${index}`}
                    className={classes.selectLabel}
                  >
                    Tipo de veículo
                  </InputLabel>
                  <Select
                    labelId={`vehicle-type-label-${index}`}
                    id={`vehicle-type-select-${index}`}
                    value={vehicle.vehicleType}
                    onChange={e =>
                      handleInputChange(
                        index,
                        'vehicleType',
                        e.target.value as SingleVehicleForm['vehicleType'],
                      )
                    }
                    displayEmpty
                    fullWidth
                    variant="outlined"
                    error={!!vehicleErrors.vehicleType}
                  >
                    <MenuItem value="" >Selecione o Tipo</MenuItem>
                    {vehiclesTypes && vehiclesTypes.map((vehicleType: any) => <MenuItem key={vehicleType.value} value={vehicleType.value}>{vehicleType.label}</MenuItem>)}
                  </Select>
                  {vehicleErrors.vehicleType && (
                    <FormHelperText error>
                      {vehicleErrors.vehicleType as string}
                    </FormHelperText>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    fontWeight="bold"
                    style={{ marginTop: '20px', marginBottom: '10px' }}
                  >
                    CRLV
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={12}>
                  <Box display="flex" alignItems="center">
                    <Typography style={{ marginRight: '1rem', accentColor: 'gray' }}>
                      Tipo de CRLV:
                    </Typography>
                    <label style={{ marginRight: '1rem', display: 'flex', alignItems: 'normal', marginBottom: '0' }}>
                      <input
                        style={{ marginRight: '4px' }}
                        type="radio"
                        checked={vehicle.crlv.isDigital === false}
                        onChange={() => {
                          handleNestedInputChange(
                            index,
                            'crlv',
                            'isDigital',
                            false,
                          );
                          validateForm();
                        }}
                      />
                      CRLV Física
                    </label>
                    <label style={{ display: 'flex', alignItems: 'normal', marginBottom: '0' }}>
                      <input
                        style={{ marginRight: '4px', accentColor: 'gray' }}
                        type="radio"
                        checked={vehicle.crlv.isDigital === true}
                        onChange={() => {
                          handleNestedInputChange(
                            index,
                            'crlv',
                            'isDigital',
                            true,
                          );
                          validateForm();
                        }}
                      />
                      CRLV Digital
                    </label>
                  </Box>
                  {crlvErrors?.isDigital && (
                    <FormHelperText error>
                      {crlvErrors.isDigital as string}
                    </FormHelperText>
                  )}
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Input
                    size="small"
                    label="Documento CRLV"
                    placeholder="00000000000"
                    value={vehicle.crlv.documentNumber}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleNestedInputChange(
                        index,
                        'crlv',
                        'documentNumber',
                        removeSpecialChars(e.target.value),
                      )
                    }
                    error={!!crlvErrors?.documentNumber}
                    helperText={crlvErrors?.documentNumber as string}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Input
                    size="small"
                    label="Vencimento CRLV (DD/MM/AAAA)"
                    placeholder="DD/MM/AAAA"
                    value={vehicle.crlv.expirationDate}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleNestedInputChange(
                        index,
                        'crlv',
                        'expirationDate',
                        e.target.value,
                      )
                    }
                    type='date'
                    error={!!crlvErrors?.expirationDate}
                    helperText={crlvErrors?.expirationDate as string}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    fontWeight="bold"
                    style={{ marginTop: '1rem', marginBottom: '0.5rem' }}
                  >
                    Anexar CRLV
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <ImageUploadField
                        label="Frente do CRLV"
                        onChange={file =>
                          handleNestedInputChange(
                            index,
                            'crlv',
                            'imageUrlFront',
                            file,
                          )
                        }
                        currentValue={vehicle.crlv.imageUrlFront}
                        error={!!crlvErrors?.imageUrlFront}
                        helperText={crlvErrors?.imageUrlFront as string}
                        acceptedFileTypes="image/jpeg,image/png,application/pdf"
                        placeholder="Anexe a frente aqui..."
                      />
                      {vehicle.crlv.imageUrlFront && String(vehicle.crlv.imageUrlFront)?.includes('http') && <a style={{ color: '#ff7206' }} href={String(vehicle.crlv.imageUrlFront)} target="_blank" rel="noopener noreferrer">Visualizar Frente da CRLV </a>}
                    </Grid>
                    { !vehicle.crlv.isDigital && <Grid item xs={12} sm={6}>
                      <ImageUploadField
                        label="Verso do CRLV"
                        onChange={file =>
                          handleNestedInputChange(
                            index,
                            'crlv',
                            'imageUrlBack',
                            file,
                          )
                        }
                        currentValue={vehicle.crlv.imageUrlBack}
                        error={!!crlvErrors?.imageUrlBack}
                        helperText={crlvErrors?.imageUrlBack as string}
                        acceptedFileTypes="image/jpeg,image/png,application/pdf"
                        placeholder="Anexe o verso aqui..."
                      />
                      {vehicle.crlv.imageUrlBack && String(vehicle.crlv.imageUrlBack)?.includes('http') && <a style={{ color: '#ff7206' }} href={String(vehicle.crlv.imageUrlBack)} target="_blank" rel="noopener noreferrer">Visualizar Verso da CRLV </a>}
                    </Grid>}
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    fontWeight="bold"
                    style={{ marginTop: '20px', marginBottom: '10px' }}
                  >
                    Imagens do veículo (Opcional)
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ImageUploadField
                    label="Imagem Frontal do Veículo"
                    onChange={file =>
                      handleNestedInputChange(
                        index,
                        'images',
                        'imageUrlFront',
                        file,
                      )
                    }
                    currentValue={vehicle.images.imageUrlFront}
                    error={!!imagesErrors?.imageUrlFront}
                    helperText={imagesErrors?.imageUrlFront as string}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ImageUploadField
                    label="Imagem Traseira do Veículo"
                    onChange={file =>
                      handleNestedInputChange(
                        index,
                        'images',
                        'imageUrlBack',
                        file,
                      )
                    }
                    currentValue={vehicle.images.imageUrlBack}
                    error={!!imagesErrors?.imageUrlBack}
                    helperText={imagesErrors?.imageUrlBack as string}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ImageUploadField
                    label="Imagem Lateral Esquerda do Veículo"
                    onChange={file =>
                      handleNestedInputChange(
                        index,
                        'images',
                        'imageUrlLeft',
                        file,
                      )
                    }
                    currentValue={vehicle.images.imageUrlLeft}
                    error={!!imagesErrors?.imageUrlLeft}
                    helperText={imagesErrors?.imageUrlLeft as string}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ImageUploadField
                    label="Imagem Lateral Direita do Veículo"
                    onChange={file =>
                      handleNestedInputChange(
                        index,
                        'images',
                        'imageUrlRight',
                        file,
                      )
                    }
                    currentValue={vehicle.images.imageUrlRight}
                    error={!!imagesErrors?.imageUrlRight}
                    helperText={imagesErrors?.imageUrlRight as string}
                  />
                </Grid>
              </Grid>
            );
          })}
          <br />
          <Grid container className={classes.formActions}>
            <ButtonComponent
              theme="outline-primary"
              flex={1}
              onClick={decideToCancelOperation}
            >
              Cancelar
            </ButtonComponent>
            <ButtonComponent
              type="submit"
              theme="primary"
              flex={1}
              disabled={!isFormValid || loading}
            >
              {loading ? 'Salvando...' : 'Salvar edição'}
            </ButtonComponent>
          </Grid>
        </form>
      </Box>

      <ModalCustom
        open={openCancelModal}
        onCancelClick={() => setOpenCancelModal(false)}
        onClickPublish={cancelOperation}
        ButtonOutlinePrimary="Cancelar"
        ButtonPrimary="Confirmar"
        alignCenter={true}
        hideCloseIcon={true}
        label="Deseja descartar os veículos que estavam sendo cadastrados nesse formulário?"
        boxStyle={{
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
          gap: 2,
        }}
      />
    </Box>
  );
};

export default CreateVehiclesForm;